import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { CandlestickChart, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refCode, setRefCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const r = searchParams.get("ref");
    if (r) { setRefCode(r.toUpperCase()); setMode("register"); }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password, refCode);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] grid-bg px-6">
      <div className="w-full max-w-md animate-in-up">
        <Link to="/" data-testid="auth-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors mb-6">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
        <div className="flex items-center justify-center gap-2 mb-8">
          <CandlestickChart size={24} className="text-blue-500" strokeWidth={2.5} />
          <span className="font-bold tracking-tight text-xl">Raein<span className="text-blue-500">Exchange</span></span>
        </div>
        <div className="bg-[#141414] border border-white/10 rounded-md p-8">
          <h1 className="text-2xl font-bold tracking-tight mb-1">
            {mode === "login" ? "Bentornato" : "Crea il tuo account"}
          </h1>
          <p className="text-sm text-zinc-400 mb-6">
            {mode === "login" ? "Accedi per vedere i segnali live." : "Iscriviti e ricevi i segnali in tempo reale."}
          </p>

          <button data-testid="google-login-btn" onClick={googleLogin}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-white text-black font-medium hover:bg-zinc-200 transition-colors mb-4">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continua con Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-zinc-500">oppure</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input data-testid="auth-name-input" type="text" placeholder="Nome" required value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-md bg-[#0A0A0A] border border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none transition-colors" />
            )}
            <input data-testid="auth-email-input" type="email" placeholder="Email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-md bg-[#0A0A0A] border border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none transition-colors" />
            <input data-testid="auth-password-input" type="password" placeholder="Password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-md bg-[#0A0A0A] border border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none transition-colors" />
            {mode === "register" && (
              <input data-testid="auth-ref-input" type="text" placeholder="Codice referral (facoltativo)" value={refCode}
                onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-2.5 rounded-md bg-[#0A0A0A] border border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none transition-colors" />
            )}
            {error && <div data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 rounded-md px-3 py-2">{error}</div>}
            <button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full px-4 py-2.5 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors disabled:opacity-50">
              {loading ? "Attendere..." : mode === "login" ? "Accedi" : "Registrati"}
            </button>
          </form>

          <p className="text-sm text-zinc-400 text-center mt-6">
            {mode === "login" ? "Non hai un account? " : "Hai già un account? "}
            <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-blue-400 hover:text-blue-300 font-medium">
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
